import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { PageHeader } from "../../../components/ui";
import dayjs from "dayjs";
import {
  ClioService,
  FilingReportItem,
  FilingReportStatusEntry,
  OpenAPI,
} from "../../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FilingReportFilters } from "./FilingReportFilters";
import { FilingReportTable } from "./FilingReportTable";
import {
  applyFilingFilters,
  resetFilters,
  uniqueStringOptions,
} from "./filtering";
import { sortFilingRows } from "./sorting";
import {
  DEFAULT_SORT_RULES,
  EMPTY_FILTERS,
  type FilingFiltersState,
  type SortRule,
} from "./types";

const MySwal = withReactContent(Swal);

function toDownloadRequest(filters: FilingFiltersState) {
  return {
    caseName: filters.caseName || null,
    taskName: filters.taskName || null,
    practiceArea: filters.practiceArea || null,
    statusFilter: filters.status || null,
    overdue: filters.overdue || null,
    createdFrom: filters.createdFrom ? filters.createdFrom.format("YYYY-MM-DD") : null,
    createdTo: filters.createdTo ? filters.createdTo.format("YYYY-MM-DD") : null,
    dueFrom: filters.dueFrom ? filters.dueFrom.format("YYYY-MM-DD") : null,
    dueTo: filters.dueTo ? filters.dueTo.format("YYYY-MM-DD") : null,
    searchTerm: filters.searchTerm.trim() || null,
  };
}

export default function FilingReport() {
  const [reportData, setReportData] = useState<FilingReportItem[]>([]);
  const [filters, setFilters] = useState<FilingFiltersState>(EMPTY_FILTERS);
  const [sortRules, setSortRules] = useState<SortRule[]>([...DEFAULT_SORT_RULES]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [savingNoteTaskId, setSavingNoteTaskId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioFilingReport()
      .then((data) => {
        setReportData(data);
        setHasLoaded(true);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading filing report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  const filterOptions = useMemo(
    () => ({
      caseName: uniqueStringOptions(reportData, "caseName"),
      taskName: uniqueStringOptions(reportData, "taskName"),
      practiceArea: uniqueStringOptions(reportData, "practiceArea"),
    }),
    [reportData]
  );

  const filteredData = useMemo(
    () => applyFilingFilters(reportData, filters),
    [reportData, filters]
  );

  const sortedData = useMemo(
    () => sortFilingRows(filteredData, sortRules),
    [filteredData, sortRules]
  );

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const uniqueCaseCount = useMemo(
    () => new Set(sortedData.map((row) => row.matterId)).size,
    [sortedData]
  );

  const applyFilters = useCallback((nextFilters: FilingFiltersState, nextSortRules: SortRule[]) => {
    setFilters(nextFilters);
    setSortRules(nextSortRules.length > 0 ? nextSortRules : [...DEFAULT_SORT_RULES]);
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(resetFilters());
    setPage(0);
  }, []);

  const handleAddStatus = async (row: FilingReportItem, status: string) => {
    if (!status) return;

    const previousRow = { ...row };
    const updatedAt = new Date().toISOString();
    const newEntry: FilingReportStatusEntry = {
      status,
      updatedAt,
      updatedByName: row.statusUpdatedByName ?? null,
    };

    setReportData((prev) =>
      prev.map((item) =>
        item.taskId === row.taskId
          ? {
              ...item,
              status,
              statusUpdatedAt: updatedAt,
              statusHistory: [...(item.statusHistory ?? []), newEntry],
            }
          : item
      )
    );

    setSavingTaskId(row.taskId);
    try {
      await ClioService.postApiClioSaveFilingReportStatuses([
        { taskId: row.taskId, matterId: row.matterId, status },
      ]);
    } catch {
      setReportData((prev) =>
        prev.map((item) => (item.taskId === row.taskId ? previousRow : item))
      );
      MySwal.fire({ icon: "error", title: <p>Error saving status.</p> });
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleSaveNote = async (row: FilingReportItem, note: string) => {
    const previousNote = row.note;

    setReportData((prev) =>
      prev.map((item) =>
        item.taskId === row.taskId ? { ...item, note: note.trim() || null } : item
      )
    );

    setSavingNoteTaskId(row.taskId);
    try {
      await ClioService.postApiClioSaveFilingReportNotes([
        {
          taskId: row.taskId,
          matterId: row.matterId,
          note,
        },
      ]);
    } catch {
      setReportData((prev) =>
        prev.map((item) =>
          item.taskId === row.taskId ? { ...item, note: previousNote ?? null } : item
        )
      );
      MySwal.fire({ icon: "error", title: <p>Error saving note.</p> });
    } finally {
      setSavingNoteTaskId(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/filingReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        // Send the filtered/sorted rows currently shown so Excel matches the table.
        body: JSON.stringify({
          ...toDownloadRequest(filters),
          items: sortedData,
        }),
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `FilingReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({ icon: "error", title: <p>Error downloading filing report.</p> });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "var(--app-content-height)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
      }}
    >
      <PageHeader
        title="Filing report"
        subtitle="Pending filing tasks assigned to Narissa Estrada."
      />

      <FilingReportFilters
        filters={filters}
        filterOptions={filterOptions}
        sortRules={sortRules}
        loading={loading}
        downloading={downloading}
        canDownload={hasLoaded && sortedData.length > 0}
        onLoad={loadReport}
        onApply={applyFilters}
        onDownload={handleDownload}
        onClear={clearFilters}
      />

      <FilingReportTable
        loading={loading}
        hasLoadedData={hasLoaded}
        uniqueCaseCount={uniqueCaseCount}
        taskCount={sortedData.length}
        rows={paginatedData}
        page={page}
        rowsPerPage={rowsPerPage}
        savingTaskId={savingTaskId}
        savingNoteTaskId={savingNoteTaskId}
        onAddStatus={handleAddStatus}
        onSaveNote={handleSaveNote}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
}
